import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Download, Eye, Calendar, Plus, Filter, Search, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const ComplianceVaultPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showGenerator, setShowGenerator] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Fetch compliance documents
      const docsRes = await fetch(`${apiUrl}/api/compliance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const docsData = await docsRes.json();
      setDocuments(docsData);

      // Fetch projects for document generation
      const projectsRes = await fetch(`${apiUrl}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectsData = await projectsRes.json();
      setProjects(projectsData.filter(p => p.status === 'active' || p.status === 'completed'));

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.documentType.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !selectedType || doc.documentType === selectedType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Compliance Vault</h1>
              <p className="text-gray-600 mt-1">Generate, manage, and download compliance documents</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/compliance/calendar')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                Calendar
              </Button>
              <Button
                onClick={() => setShowGenerator(true)}
                className="text-white"
                style={{ background: '#14B8A6' }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Generate Document
              </Button>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <AlertBanner
          type="info"
          message="All documents are auto-generated with audit trails and version control. Download as PDF for submission."
          className="mb-6"
        />

        {/* Search & Filter */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="CSR-1">CSR-1</option>
              <option value="Impact Assessment">Impact Assessment</option>
              <option value="Utilization Certificate">Utilization Certificate</option>
              <option value="Board Report">Board Report</option>
              <option value="Annual Action Plan">Annual Action Plan</option>
            </select>
          </div>
        </div>

        {/* Documents Grid */}
        {filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Yet</h3>
            <p className="text-gray-600 mb-6">Generate your first compliance document to get started</p>
            <Button
              onClick={() => setShowGenerator(true)}
              className="text-white"
              style={{ background: '#14B8A6' }}
            >
              <Plus className="w-5 h-5 mr-2" />
              Generate Document
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((doc) => (
              <DocumentCard key={doc.id} document={doc} />
            ))}
          </div>
        )}

        {/* Document Generator Modal */}
        {showGenerator && (
          <DocumentGeneratorModal
            projects={projects}
            onClose={() => setShowGenerator(false)}
            onGenerate={fetchData}
          />
        )}
      </div>
    </div>
  );
};

// Document Card Component
const DocumentCard = ({ document }) => {
  const getDocumentIcon = (type) => {
    return <FileText className="w-6 h-6" />;
  };

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-6">
      <div className="flex items-start justify-between mb-4">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: '#14B8A620' }}
        >
          {getDocumentIcon(document.documentType)}
        </div>
        <StatusBadge status={document.status} size="sm" />
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2">{document.documentType}</h3>
      <p className="text-sm text-gray-600 mb-4">FY {document.financialYear}</p>

      <div className="space-y-2 text-sm text-gray-600 mb-4">
        <div className="flex items-center justify-between">
          <span>Version:</span>
          <span className="font-semibold">{document.version}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Generated:</span>
          <span className="font-semibold">{new Date(document.generatedAt).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
        >
          <Eye className="w-4 h-4 mr-1" />
          View
        </Button>
        <Button
          size="sm"
          className="flex-1 text-white"
          style={{ background: '#14B8A6' }}
        >
          <Download className="w-4 h-4 mr-1" />
          Download
        </Button>
      </div>
    </div>
  );
};

// Document Generator Modal
const DocumentGeneratorModal = ({ projects, onClose, onGenerate }) => {
  const [documentType, setDocumentType] = useState('CSR-1');
  const [selectedProject, setSelectedProject] = useState('');
  const [financialYear, setFinancialYear] = useState('2024-25');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const payload = {
        projectId: selectedProject || null,
        documentType,
        financialYear
      };

      await fetch(`${apiUrl}/api/compliance/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      onGenerate();
      onClose();
    } catch (error) {
      console.error('Error generating document:', error);
    } finally {
      setGenerating(false);
    }
  };

  const requiresProject = documentType !== 'Annual Action Plan';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Generate Document</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Document Type</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="CSR-1">CSR-1 Form</option>
              <option value="Impact Assessment">Impact Assessment Report</option>
              <option value="Utilization Certificate">Utilization Certificate</option>
              <option value="Board Report">Board Report Annexure</option>
              <option value="Annual Action Plan">Annual Action Plan</option>
            </select>
          </div>

          {requiresProject && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Project</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Choose a project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title} ({project.projectCode})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Financial Year</label>
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="2024-25">2024-25</option>
              <option value="2023-24">2023-24</option>
              <option value="2022-23">2022-23</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            className="flex-1 text-white"
            style={{ background: '#14B8A6' }}
            disabled={generating || (requiresProject && !selectedProject)}
          >
            {generating ? <LoadingSpinner size="sm" /> : 'Generate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceVaultPage;